import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Chip, Alert, CircularProgress, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';

const API = '/api/rrhh';
const fmt = v => Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const periodos = () => {
  const list = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    list.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return list;
};

export default function AdelantosRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [trabajadores, setTrabajadores] = useState([]);
  const [adelantos, setAdelantos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('PENDIENTE');

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ trabajador: null, periodo: periodos()[0], monto: '', descripcion: '' });
  const [saving, setSaving] = useState(false);

  const [anularId, setAnularId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresa) return;
    axios.get(`${API}/trabajadores?empresa_id=${empresa.empresa_id}&activos=true`)
      .then(r => setTrabajadores(r.data)).catch(() => {});
  }, [empresa]);

  const cargar = useCallback(() => {
    if (!empresa) return;
    setLoading(true); setError('');
    axios.get(`${API}/adelantos?empresa_id=${empresa.empresa_id}&estado=${estadoFiltro}`)
      .then(r => setAdelantos(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [empresa, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrear = async () => {
    if (!form.trabajador || !form.monto) return;
    setSaving(true);
    try {
      await axios.post(`${API}/adelantos`, {
        empresa_id: empresa.empresa_id,
        trabajador_id: form.trabajador.id,
        periodo: form.periodo,
        monto: form.monto,
        descripcion: form.descripcion,
      });
      setDlgOpen(false);
      setForm({ trabajador: null, periodo: periodos()[0], monto: '', descripcion: '' });
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear adelanto');
    } finally { setSaving(false); }
  };

  const handleAnular = async () => {
    try {
      await axios.put(`${API}/adelantos/${anularId}/anular`);
      setAnularId(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al anular');
    }
  };

  const total = adelantos.filter(a => a.estado === 'PENDIENTE').reduce((s, a) => s + Number(a.monto), 0);

  const estadoColor = { PENDIENTE: 'warning', DESCONTADO: 'success', ANULADO: 'error' };

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" color="purple" gutterBottom>
        Adelantos de Remuneraciones
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Autocomplete
            options={empresas} value={empresa} onChange={(_, v) => setEmpresa(v)}
            getOptionLabel={o => o.razon_social || ''}
            sx={{ minWidth: '16em' }}
            renderInput={p => <TextField {...p} label="Empresa" size="small"
              InputProps={{ ...p.InputProps, startAdornment: <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'purple' }} /> }} />}
          />
          <TextField select label="Estado" size="small" value={estadoFiltro}
            onChange={e => setEstadoFiltro(e.target.value)} sx={{ minWidth: '10em' }}>
            {['PENDIENTE', 'DESCONTADO', 'ANULADO', 'TODOS'].map(e => (
              <MenuItem key={e} value={e}>{e}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: 'purple' }}
            onClick={() => setDlgOpen(true)} disabled={!empresa}>
            Nuevo Adelanto
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {empresa && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f3e5f5', border: '1px solid purple' }}>
          <Typography variant="subtitle2" color="purple">
            Adelantos pendientes de descuento: <strong>S/ {fmt(total)}</strong>
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              ({adelantos.filter(a => a.estado === 'PENDIENTE').length} adelanto(s) — se descontarán automáticamente al calcular la planilla del período)
            </Typography>
          </Typography>
        </Paper>
      )}

      <TableContainer component={Paper}>
        {loading ? <Box p={3} textAlign="center"><CircularProgress color="secondary" /></Box> : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'purple' }}>
                {['Trabajador', 'Período', 'Monto', 'Descripción', 'Estado', 'Registrado por', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {adelantos.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">No hay adelantos</TableCell></TableRow>
              ) : adelantos.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontSize: 12 }}>{a.apellido_paterno} {a.apellido_materno}, {a.nombres}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{a.periodo}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>S/ {fmt(a.monto)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{a.descripcion || '—'}</TableCell>
                  <TableCell>
                    <Chip label={a.estado} color={estadoColor[a.estado] || 'default'} size="small" />
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: '#666' }}>{a.usuario_reg}</TableCell>
                  <TableCell>
                    {a.estado === 'PENDIENTE' && (
                      <IconButton size="small" color="error" title="Anular" onClick={() => setAnularId(a.id)}>
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Dialog Nuevo Adelanto */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'purple', color: 'white' }}>Registrar Adelanto</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Autocomplete
            options={trabajadores} value={form.trabajador}
            onChange={(_, v) => setForm(f => ({ ...f, trabajador: v }))}
            getOptionLabel={o => `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}`}
            sx={{ minWidth: '16em' }}
            renderInput={p => <TextField {...p} label="Trabajador *" size="small" />}
          />
          <TextField select label="Período *" size="small" value={form.periodo}
            onChange={e => setForm(f => ({ ...f, periodo: e.target.value }))} sx={{ minWidth: '16em' }}>
            {periodos().map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField label="Monto (S/) *" size="small" type="number" value={form.monto}
            onChange={e => setForm(f => ({ ...f, monto: e.target.value }))}
            inputProps={{ min: 0, step: '0.01' }} sx={{ minWidth: '16em' }} />
          <TextField label="Descripción" size="small" value={form.descripcion}
            onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
            multiline rows={2} sx={{ minWidth: '16em' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" sx={{ bgcolor: 'purple' }} onClick={handleCrear} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm anular */}
      <Dialog open={!!anularId} onClose={() => setAnularId(null)}>
        <DialogTitle>¿Anular adelanto?</DialogTitle>
        <DialogContent>
          <Typography>El adelanto no será descontado en la planilla. Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnularId(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleAnular}>Anular</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
